import { makeAutoObservable } from 'mobx';

import * as Sentry from '@sentry/nextjs';
import subscriptionGroupService, {
  ISubscriptionGroupQuery,
} from '@/services/subscriptionGroupService';
import { Group } from '@/types/Content/Group';
import { FilterValues } from '@/domains/Groups/constants/FilterValues';
import { HTTPListResponse } from '@/types/HTTPListResponse';

export type SubscriptionGroupStoreHydration = {
  data?: HTTPListResponse<Group>;
  params?: ISubscriptionGroupQuery;
};
type Meta = {
  total: number;
  lastCacheDate: number;
  next: string;
};

class SubscriptionGroupStore {
  protected service = subscriptionGroupService;

  subscriptionGroupList: Group[] = [];

  subscriptionGroupListWithLifeEvents: Group[] = [];

  subscriptionGroupQuery: ISubscriptionGroupQuery = {
    page: 0,
    tags: [],
    contentTypeList: '',
    contentTrending: true,
    includeTotalCount: true,
  };

  meta: Meta = {
    total: 0,
    lastCacheDate: null,
    next: null,
  };

  metaWithLifeEvents: Meta = {
    total: 0,
    lastCacheDate: null,
    next: null,
  };

  subscriptionGroupCurrent: Group = null;

  loading = {
    getAll: false,
    getAllWithLifeEvents: false,
    getOne: false,
    leaveJoin: false,
  };

  constructor() {
    makeAutoObservable(this);
  }

  setGroupList(data: HTTPListResponse<Group>) {
    const { results, totalCount, next } = data;
    this.meta.total = totalCount;
    this.meta.next = next;
    this.meta.lastCacheDate = Date.now();
    this.subscriptionGroupList = results;
  }

  setGroupListWithLifeEvents(data: HTTPListResponse<Group>) {
    const { results, totalCount } = data;
    this.metaWithLifeEvents.total = totalCount;
    this.metaWithLifeEvents.lastCacheDate = Date.now();
    this.subscriptionGroupListWithLifeEvents = results;
  }

  async getGroup(
    slug: string,
    contentRelatedTags: Array<string> = [],
    filter: Array<FilterValues>,
    trending = true,
  ): Promise<Group> {
    this.loading.getOne = true;
    try {
      this.subscriptionGroupCurrent = await this.service.getGroup(
        slug,
        contentRelatedTags,
        filter,
        trending,
      );

      return this.subscriptionGroupCurrent;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    } finally {
      this.loading.getOne = false;
    }
  }

  async getAllGroups(params): Promise<Array<Group>> {
    if (
      this.subscriptionGroupList.length &&
      this.meta.lastCacheDate &&
      JSON.stringify(this.subscriptionGroupQuery) === JSON.stringify(params) &&
      Date.now() - this.meta.lastCacheDate <= 5 * 60 * 1000
    ) {
      return this.subscriptionGroupList;
    }
    this.loading.getAll = true;
    this.subscriptionGroupQuery = { ...params };

    try {
      const data = await this.service.getAll(this.subscriptionGroupQuery);

      this.setGroupList(data);
      return this.subscriptionGroupList;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    } finally {
      this.loading.getAll = false;
    }
  }

  async getAllGroupsWithLifeEvents() {
    this.loading.getAllWithLifeEvents = true;

    if (
      this.subscriptionGroupListWithLifeEvents.length &&
      this.metaWithLifeEvents.lastCacheDate &&
      Date.now() - this.metaWithLifeEvents.lastCacheDate <= 5 * 60 * 1000
    ) {
      this.loading.getAllWithLifeEvents = false;
      return this.subscriptionGroupListWithLifeEvents;
    }

    try {
      const data = await this.service.getAllWithLifeEvents();
      this.setGroupListWithLifeEvents(data);
      return this.subscriptionGroupListWithLifeEvents;
    } catch (error) {
      Sentry.captureException(error);
      return null;
    } finally {
      this.loading.getAllWithLifeEvents = false;
    }
  }

  hydrate(data: SubscriptionGroupStoreHydration) {
    this.subscriptionGroupQuery = {
      ...this.subscriptionGroupQuery,
      ...data.params,
    };
    this.setGroupList(data.data);
  }
}

export default new SubscriptionGroupStore();
